import { createApiClient } from '@multaqa/api-client'
import { i18next } from '@multaqa/i18n'

/**
 * The API origin, with no trailing slash and no `/api` prefix.
 *
 * `VITE_API_URL` is the ORIGIN. Every raw-fetch call site in this app appends
 * `/api` to it itself, and so does the typed client below.
 */
export const apiOrigin = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')

/**
 * Global 401 handler.
 *
 * Full navigation rather than a router push: the session is gone, so
 * everything in memory — query cache, stores — should go with it.
 *
 * `/two-factor` is excluded alongside `/login`: a 401 there is expected — the
 * admin passed the password step but has not yet verified their TOTP code,
 * so `/platform/me` correctly rejects the request. The route guard catches
 * that 401 and renders the challenge form.
 *
 * This handler runs before the `_authenticated` guard can redirect, so it
 * carries the current address as `redirect` itself: a deep link (the Support
 * MCP's Connect page) must come back after sign-in, not land on the
 * dashboard. The login form origin-checks the value (`redirectTarget`).
 */
export function handleUnauthorized(): void {
  const { pathname, search, href } = window.location
  if (pathname === '/login' || pathname === '/two-factor') {
    return
  }
  window.location.assign(
    pathname === '/' && search === '' ? '/login' : `/login?redirect=${encodeURIComponent(href)}`,
  )
}

/**
 * WHY THE TYPED BASE URL IS NOT SIMPLY `apiOrigin`
 *
 * The API serves three route families at three different depths:
 *
 *   /api/<spec path>        module routes  -> the typed client
 *   /sanctum/csrf-cookie    the CSRF cookie
 *   /login, /logout, ...    Fortify, on the `web` group
 *
 * The generated `paths` in the api-client are relative to the spec's server
 * URL, which already ends in `/api`, so the keys read `/accounting/accounts`.
 * A cross-origin build therefore needs `<origin>/api` for typed calls and a
 * bare `<origin>` for the other two.
 *
 * In development `apiOrigin` is empty and Vite's proxy maps all three families
 * itself, so the paths the client emits do not have to be complete. That is
 * exactly why a wrong production value produced no failing test: no test and
 * no dev session ever built a complete URL. The regression guard for this
 * shared client lives in the company app, at `src/lib/api-origin.test.ts`.
 */
export const api = createApiClient({
  baseUrl: apiOrigin === '' ? '' : `${apiOrigin}/api`,
  authOrigin: apiOrigin,
  headers: () => ({ 'Accept-Language': (i18next.language ?? 'en').split('-')[0] ?? 'en' }),
  onUnauthorized: handleUnauthorized,
})
